import React, { useCallback, useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import { GenericTextInput, GenericDropdown, GenericCheckbox } from '../../shared/components';
import { LicenseType, createInputMask, LicenseStatus } from '@trip-ux/constants';
import { DropdownType, Progress } from '@trip-ux/components';
import { MIN_DATE, futureDate } from './LicenseLabels';
import LicenseActionButton from './licenseActionButtons/LicenseActionButton';
import UpdateAgencyLicenseFormFields from './UpdateAgencyLicenseFormFields';
import UpdateAgentLicenseFormFields from './UpdateAgentLicenseFormFields';
import { dayjs } from '@trip-ux/common';
import { useLocation } from 'react-router-dom';
import { retrieveAgentListing } from '@trip-ux/redux-store';
import { useDispatch } from 'react-redux';
import { MaintenanceDatePicker } from '../../shared/GenericComponents';
import { get } from 'lodash';
const MAX_CHARACTERS = 255;

const alphabeticalInputMask = createInputMask(/^[a-zA-Z0-9-]+$/, MAX_CHARACTERS);

const LicenseFormFields = ({
  values,
  handleSubmit,
  disableSubmit,
  form,
  suggestedAgents,
  organizationSearch,
  setAgentOptions,
  licenseInfo,
  organizationDropDownList,
  language,
  agentList,
  filterProvinceList,
  labels
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const licenseType = queryParams.get('licenseType');
  const response = licenseInfo?.data?.response;
  const license = response?.license;
  const filterMap = {
    [LicenseStatus.IN_PROGRESS]: LicenseStatus.RENEWAL_PENDING
  };

  const formLabels = get(labels, 'license_info.add_license.page.formLabels');
  const calendarLabels = get(labels, 'license_info.add_license.page.calendarLabels');
  const statusOptions = get(labels, 'license_info.add_license.page.statusOptions');
  const screeningStatusOptions = get(labels, 'license_info.add_license.page.screeningStatusOptions');
  const licenseTypeOptions = get(labels, 'license_info.add_license.page.licenseTypeOptions');

  const fieldLabels = { calendarLabels, formLabels, datePickerLabels: calendarLabels };
  const [employmentType, setEmploymentType] = useState('');

  const filteredStatus = statusOptions.filter(option => option.value !== filterMap[license?.status]);

  const changeValue = (value: string) => {
    // Reset the form with the new licenseType and clear other fields
    form.change('licenseType', value);
    form.batch(() => {
      // Clear all fields except specified ones
      Object.keys(values).forEach(fieldName => {
        if (!['licenseType', 'expiredDate', 'effectiveDate', 'screeningEffectiveDate'].includes(fieldName)) {
          if (form.getRegisteredFields().includes(fieldName)) {
            form.change(fieldName, '');
            form.resetFieldState(fieldName);
          }
        }
      });
      // Set specific date fields to "01-01-9999"
      ['effectiveDate', 'expiredDate', 'screeningEffectiveDate'].forEach(dateField => {
        if (form.getRegisteredFields().includes(dateField)) {
          const fieldState = form.getFieldState(dateField);
          if (fieldState?.value) {
            fieldState.change(futureDate);
          }
        }
      });
    });
  };

  const changeProfileRef = (value: string) => {
    const newVal = String(value);
    form.change('profileRef', newVal);
    const agent = agentList.find(profile => profile.value === value);
    if (agent) {
      setEmploymentType(agent?.employmentType);
    }
  };

  useEffect(() => {
    if (values?.organizationCode) {
      dispatch(retrieveAgentListing(values.organizationCode));
    }
  }, [values?.organizationCode, dispatch]);

  useEffect(() => {
    if (response?.employmentType) {
      setEmploymentType(response?.employmentType);
    }
  }, [response]);

  const uiDateFormat = calendarLabels?.dateFormat;

  const expiryDateDisabledDays = {
    before:
      values?.effectiveDate && uiDateFormat
        ? dayjs(values?.effectiveDate, uiDateFormat, true).toDate()
        : undefined
  };
  const effectiveDateDisabledDays = {
    after:
      values?.expiredDate && uiDateFormat ? dayjs(values?.expiredDate, uiDateFormat, true).toDate() : undefined,
    before: MIN_DATE
  };

  const AGENT = 'AGENT';

  return (
    <>
      {licenseInfo?.isLoading && <Progress />}
      <form onSubmit={handleSubmit} className="license-form-container" data-testid="licenseinfo-form">
        {licenseType === LicenseType.AGENCY && (
          <UpdateAgencyLicenseFormFields licenseInfo={licenseInfo} language={language} labels={labels} />
        )}
        {licenseType === LicenseType.AGENT && (
          <UpdateAgentLicenseFormFields licenseInfo={licenseInfo} language={language} labels={labels} />
        )}
        {licenseType && <div className="dividing-line" />}
        {!licenseType && (
          <div className="d-flex flex-row flex-wrap">
            <div className={`mb-auto space-between form-field`}>
              <Field
                component={GenericDropdown}
                name="licenseType"
                id="licenseType"
                labels={fieldLabels}
                options={licenseTypeOptions}
                showLabel={true}
                defaultValue={AGENT}
                language={language}
                onChange={changeValue}
              />
            </div>
            <div className={`mb-auto space-between form-field`}>
              <Field
                component={GenericDropdown}
                name="organizationCode"
                id="organizationCode"
                labels={fieldLabels}
                showLabel={true}
                language={language}
                variant={DropdownType.AUTO_SUGGEST}
                options={organizationDropDownList}
                onAutoSuggestChange={organizationSearch}
                shouldSuggestWithoutTyping={true}
                validateOnActive={false}
                value={values?.organizationCode}
              />
            </div>
          </div>
        )}
        <div className="d-flex flex-row flex-wrap">
          {!licenseType && values?.licenseType?.toUpperCase() === LicenseType.AGENT && (
            <div className={`mb-auto space-between form-field`}>
              <Field
                component={GenericDropdown}
                name="profileRef"
                id="profileRef"
                labels={fieldLabels}
                showLabel={true}
                language={language}
                options={suggestedAgents}
                variant={DropdownType.AUTO_SUGGEST}
                onAutoSuggestChange={e => setAgentOptions(e)}
                validateOnActive={false}
                shouldSuggestWithoutTyping={true}
                value={String(values?.profileRef)}
                onChange={changeProfileRef}
              />
            </div>
          )}
          <Field
            component={GenericTextInput}
            className="form-field"
            name="licenseNumber"
            labels={formLabels}
            showLabel={true}
            language={language}
            mask={alphabeticalInputMask}
          />
        </div>
        <div className="d-flex flex-row flex-wrap">
          <Field
            component={GenericTextInput}
            className="form-field"
            name="legalName"
            labels={formLabels}
            showLabel={true}
          />
          <div className={`mb-auto space-between form-field`}>
            <Field
              component={GenericDropdown}
              name="licensedProvince"
              id="licensedProvince"
              labels={fieldLabels}
              options={filterProvinceList(values, employmentType)}
              showLabel={true}
              language={language}
              isDisabled={licenseType}
            />
          </div>
        </div>
        <div className="d-flex flex-row flex-wrap">
          <div className="mb-auto space-between form-field">
            <Field
              name="effectiveDate"
              key="effectiveDate-field"
              component={MaintenanceDatePicker}
              labels={fieldLabels}
              showLabel={true}
              ariaLabel={formLabels.effectiveDate}
              disabledDays={effectiveDateDisabledDays}
            />
          </div>
          <div className="mb-auto space-between form-field">
            <Field
              name="expiredDate"
              key="expiredDate-field"
              component={MaintenanceDatePicker}
              labels={fieldLabels}
              showLabel={true}
              ariaLabel={formLabels.expiredDate}
              disabledDays={expiryDateDisabledDays}
            />
          </div>
        </div>
        <div className="d-flex flex-row flex-wrap">
          <div className="mb-auto space-between form-field">
            <Field
              name="screeningEffectiveDate"
              key="screeningEffectiveDate-field"
              component={MaintenanceDatePicker}
              labels={fieldLabels}
              showLabel={true}
              ariaLabel={formLabels.screeningEffectiveDate}
              disabledDays={{ before: MIN_DATE }}
            />
          </div>
          <div className={`mb-auto space-between form-field`}>
            <Field
              component={GenericDropdown}
              name="screeningStatus"
              id="screeningStatus"
              labels={fieldLabels}
              options={screeningStatusOptions}
              showLabel={true}
              language={language}
            />
          </div>
        </div>
        <div className="d-flex flex-row flex-wrap">
          <div className={`mb-auto space-between form-field`}>
            <Field
              component={GenericDropdown}
              name="status"
              id="status"
              labels={fieldLabels}
              options={filteredStatus}
              showLabel={true}
              language={language}
            />
          </div>
        </div>
        {licenseType && license?.isDeleted && (
          <div className="d-flex flex-row flex-wrap">
            <Field
              className="mb-auto space-between form-field delete-field"
              id="isDeleted"
              name="isDeleted"
              type="select"
              validateOnBlur
              showError
              component={GenericCheckbox}>
              {formLabels.deleted}
            </Field>
          </div>
        )}
        <LicenseActionButton
          licenseError={licenseInfo?.error}
          handleSubmit={handleSubmit}
          disableSubmit={disableSubmit}
          labels={labels}
        />
      </form>
    </>
  );
};

export default LicenseFormFields;
